import type {
    PartnerBenefit,
    PartnerFaqItem,
    PartnerOffering,
    PartnerStep,
    PartnerVetting,
    TrustStat,
} from './types'

// The published conversion path: draft recipient AND the visible fallback address.
// Keep it a monitored team alias, never a personal address.
export const PARTNERSHIPS_EMAIL = 'partnerships@wanderu.com'

// Bounds the composed mailto URL: some mail clients truncate or reject URLs past
// ~2000 characters, and a free-text details field would otherwise carry an arbitrary
// length into it.
export const DETAILS_MAX_LENGTH = 800

export const DETAILS_PLACEHOLDER =
    'Where do you run? What does your fleet look like? Anything that makes you different?'

export const benefits: PartnerBenefit[] = [
    {
        title: 'Millions of Travelers',
        description:
            'Tap into millions of searches across Wanderu’s network every month. Your routes, in front of the right customers.',
    },
    {
        title: 'National Network',
        description:
            'Sell tickets alongside other top partners on a platform travelers already trust. Feeder routes connect your service to the rest of the country.',
    },
    {
        title: 'We Handle the Technology',
        description:
            'No custom API required. We integrate with your existing systems — whether you use a distribution provider or your own setup.',
    },
]

export const offerings: PartnerOffering[] = [
    {
        title: 'Demand Intelligence',
        description:
            'See where travelers are searching for your routes. Corridor-level search volume shows you exactly where demand exists.',
    },
    {
        title: 'Price Compass',
        description:
            'Benchmark your fares against competitors on shared corridors. Know if you’re priced to win or leaving money on the table.',
    },
    {
        title: 'Route Opportunities',
        description:
            'Discover unserved city-pairs with proven search demand. Find the routes travelers want that nobody runs yet.',
    },
]

export const trustStats: TrustStat[] = [
    { stat: '500+', label: 'partners' },
    { stat: '$0', label: 'upfront cost' },
]

export const steps: PartnerStep[] = [
    {
        title: 'Reach Out',
        description:
            'Send us an email with your company name and a little about your service. That’s all it takes to start the conversation.',
    },
    {
        title: 'Compliance Review',
        description:
            'We verify your DOT credentials, insurance, and compliance through FMCSA records.',
    },
    {
        title: 'Partnership Agreement',
        description:
            'Simple, straightforward terms. We’ll walk you through everything during the review.',
    },
    {
        title: 'Integration',
        description:
            'Our team connects your schedules to Wanderu, whether you use a distribution provider or your own system.',
    },
    {
        title: 'Go Live',
        description: 'Your routes appear on Wanderu and travelers start booking.',
    },
]

export const vetting: PartnerVetting = {
    items: [
        'Active DOT number and MC authority',
        'Proof of insurance (minimum required limits)',
        'Compliance history (FMCSA records)',
        'Driver qualifications and safety record',
    ],
    closing: 'We’re transparent about our vetting because trust matters in this industry.',
}

export const faqs: PartnerFaqItem[] = [
    {
        question: 'Does it cost anything to join?',
        answer: 'No upfront cost. Wanderu operates on a commission model — we only earn when you do.',
    },
    {
        question: 'Do I need a custom API?',
        answer: 'Not necessarily. We integrate with existing booking systems and distribution providers.',
    },
    {
        question: 'How long does it take?',
        answer: 'Most partners go live within 4–8 weeks of first reaching out.',
    },
    {
        question: 'What is the commission rate?',
        answer: 'Rates vary by partner and volume. We’ll discuss specifics during the review process.',
    },
]
